/* eslint no-tabs: "off", indent: "off", no-case-declarations: "off", no-lonely-if: "off" */

import React from "react";
import _ from "underscore";

import { SET_PROJECT_THEME } from "actions/constants.js";

import { defaultContentColumns } from "lib/table/contentColumns.js";
import { defaultSearchHeaders } from "lib/table/mediaColumns.js";
import { contentTabBase } from "lib/table/contentTabColumns.js";
import { defaultConfigureStats, defaultCrosstabsNarrativeHeaders } from "lib/table/crosstabsColumns.js";

// Set static default variables, so that when a project is changed it won't keep the old project's variables
const graphTabSelectorColumns = [
	{
		name: "Narrative",
		id: "name",
		width: 5,
		fixed: false,
	},
	{
		name: "Perspective",
		id: "perspective",
		width: 100,
		fixed: true,
	},
	{
		name: "Volatility",
		id: "volatility",
		width: 150,
		fixed: true,
	},
];
const hotArticleLimit = {
	hot: 100,
	hotter: 300,
	hottest: 1000,
};

const mapSourceTooltipText =
	"The deviation from the average number of published articles in a state or region that support a given narrative.";
const mapAudienceTooltipText = "The deviation from the average reaction to articles in a state or region that support a given narrative.";
const mapEventTooltipText = "The volume of events in a region, relative to the population in that region.";
const mapSpendingTooltipText = "The total spending in a state or region.";

const graphAudienceTooltipText = "The volume is the level of reaction to articles that support a given narrative";
const graphEventTooltipText = "Total number of events of a given type";
const graphSourceTooltipText = "The volume is the number of published articles that support a given narrative.";
const graphSpendingTooltipText = "Total spending over time.";

const timezone = {
	name: "Greenwich Mean Time",
	offsetDisplay: "0:00",
	offset: 0, // in minutes
};

// Project config variables
const initialState = {
	additionalLegendHeaders: [],
	aiToggle: false,
	analystSystem: false,
	altProjectForCrosstabsNarratives: false,
	alwaysShowContentInteractions: false,
	availableTabs: {},
	chatConfig: {},
	documentChatConfig: null,
	multiDocumentChatConfig: null,
	compareMultiSelect: false,
	contentTabCsvHeaders: [],
	contentTabDateBegin: false,
	contentTabDateEnd: false,
	contentTabEntityFilters: false,
	contentTabMap: false,
	configureStats: defaultConfigureStats,
	contentTabTableHeaders: contentTabBase, // just so there's something
	contentColumns: defaultContentColumns,
	crosstabsBargraphTopLeft: false,
	crosstabsBargraphMiddleTopLeft: false,
	crosstabsBargraphMiddleLeft: false,
	crosstabsBargraphMiddleBottomLeft: false,
	crosstabsBargraphBottomLeft: false,
	crosstabsBargraphTopRight: false,
	crosstabsBargraphMiddleRight: false,
	crosstabsBargraphBottomRight: false,
	crosstabsNarrativeFacets: false,
	crosstabsNarrativeHeaderLabels: false,
	crosstabsNarrativeHeaders: defaultCrosstabsNarrativeHeaders,
	darkUI: true, // Boolean. False enables light UI
	dataCatalogSupport: false,
	defaultAggregationLevel: "timeseries",
	defaultEntityType: "Persons",
	defaultOriginalLanguage: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: [],
	defaultZoom: false,
	desiredCrosstabsNarrativeOrder: [],
	disableFilters: false,
	disableFiltersMessage: false,
	disableGeneralDownload: false,
	dmaMap: false,
	duplicateToggle: false,
	emailSubscribeOption: false,
	entityFiltersAvailable: false, // sidebar entity filters
	entityLanguage: false,
	eventColumns: false, // false or array of objects.
	exoveraEditButton: false,
	expandedContentShowTweets: false,
	fakeSingleArticle: false,
	forceCrosstabsEntity: "",
	gaMeasurementId: undefined,
	graphDataTypes: false,
	graphTabFacets: false,
	graphTabSelectorColumns,
	hideArticlesExport: false,
	hideEntities: false, // No entities at all for this project
	hideEntitiesInAIMode: false,
	hideMap: false,
	hideMapFilterIds: [],
	hideMapIndicatorInGraph: false,
	hideShare: false,
	hideParentNarrativeTags: false,
	highlightedEntitiesTitle: "",
	hotArticleLimit,
	logRocketAppId: undefined,
	mapCountryIds: [],
	mapLookup: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: false, // false for 'effectiveness'
	noUserSearches: false,
	noReaction: false,
	percentageTypes: false,
	projectMapConfig: {}, // sets route specific data, ie, loads map at a certain point in a certain tab
	projectTheme: false,
	projectNotifications: [],
	projectSpecificTagFormat: false,
	mediaEntityFilters: false,
	mediaPredictiveSelect: false,
	mediaSearchHeaders: defaultSearchHeaders,
	mediaLanguages: ["English"],
	googleTranslateLanguages: ["en"],
	mediaCountries: ["United States"],
	mediaTranslateTitles: false,
	narrativeEntityTranslateToggle: false,
	queryBuilderSupported: false,
	relevancySlider: false,
	explorerDatasets: [],
	explorerShowEntities: [],
	showAllTopicsMap: false,
	showTopicsInGraph: true,
	statsPDF: false,
	timezone,
	topPhrasesAvailable: false,
	topicsPie: false,
	topicsPieArray: false,
	topicStatsConfig: false, // or [{}]
	uiThemeToggle: false,
	useGlobalMaps: false,
	useInternalTranslation: true,
	userSearchHideLocation: false,
	videoTabEntities: false,
	videoTabEntityGraph: false,
	videoTabEndDate: "",
	videoTabStartDate: "",
	videoTabTopics: false,
	webPagesCollections: undefined,

	// Tooltip text
	mapSourceTooltipText,
	mapAudienceTooltipText,
	mapEventTooltipText,
	mapSpendingTooltipText,

	graphAudienceTooltipText,
	graphEventTooltipText,
	graphSourceTooltipText,
	graphSpendingTooltipText,
};

const projectConfig = (state = initialState, action) => {
	switch (action.type) {
		case "configProject":
			// console.log("===== configProject =====", action);
			// build graphTabActiveFacets, if graphTabFacets exist.
			// First option is default
			let graphTabActiveFacets = false;
			if (action.graphTabFacets) {
				graphTabActiveFacets = {};
				_.each(action.graphTabFacets, (facet) => {
					graphTabActiveFacets[facet.id] = facet.options[0];
				});
			}

			// NOTE: Don't default to state, or the individual project variables will mix.
			return Object.assign({}, state, {
				additionalLegendHeaders: action.additionalLegendHeaders || [],
				aiToggle: action.aiToggle || false,
				aiToggleNoReaction: action.aiToggleNoReaction || false,
				altProjectForCrosstabsNarratives: action.altProjectForCrosstabsNarratives || false,
				alwaysShowContentInteractions: !!action.alwaysShowContentInteractions,
				analystSystem: action.analystSystem || false,
				availableTabs: action.availableTabs || {},
				chatConfig: action.chatConfig || {},
				documentChatConfig: action.documentChatConfig || null,
				multiDocumentChatConfig: action.multiDocumentChatConfig || null,
				compareHeaderConfig: action.compareHeaderConfig || [
					{ id: "self", isHeader: true }, // isolate All Topics, if it exists
					{ id: "narratives", header: "Narratives" },
					{ id: "additionalSearches", header: "Other Trends" },
					{ id: "events", header: "Events" },
				],
				compareMultiSelect: !!action.compareMultiSelect,
				configureStats: action.configureStats || defaultConfigureStats,
				contentColumns: action.contentColumns || defaultContentColumns,
				contentTabCsvHeaders: action.contentTabCsvHeaders || [],
				contentTabDateBegin: action.contentTabDateBegin,
				contentTabDateEnd: action.contentTabDateEnd,
				contentTabMap: !!action.contentTabMap,
				contentTabTableHeaders: action.contentTabTableHeaders || contentTabBase,
				crosstabsBargraphTopLeft: action.crosstabsBargraphTopLeft || false,
				crosstabsBargraphMiddleTopLeft: action.crosstabsBargraphMiddleTopLeft || false,
				crosstabsBargraphMiddleLeft: action.crosstabsBargraphMiddleLeft || false,
				crosstabsBargraphMiddleBottomLeft: action.crosstabsBargraphMiddleBottomLeft || false,
				crosstabsBargraphBottomLeft: action.crosstabsBargraphBottomLeft || false,
				crosstabsBargraphTopRight: action.crosstabsBargraphTopRight || false,
				crosstabsBargraphMiddleRight: action.crosstabsBargraphMiddleRight || false,
				crosstabsBargraphBottomRight: action.crosstabsBargraphBottomRight || false,
				crosstabsNarrativeFacets: action.crosstabsNarrativeFacets || false,
				crosstabsNarrativeHeaderLabels: action.crosstabsNarrativeHeaderLabels || false,
				crosstabsNarrativeHeaders: action.crosstabsNarrativeHeaders || defaultCrosstabsNarrativeHeaders,
				darkUI: action.darkUI,
				dataCatalogSupport: action.dataCatalogSupport,
				defaultAggregationLevel: action.defaultAggregationLevel, // default set in action, since this one affects multiple reducers
				defaultEntityType: action.defaultEntityType || "Persons",
				defaultOriginalLanguage: !!action.defaultOriginalLanguage,
				defaultLightUI: !!action.defaultLightUI,
				defaultSearchTranslationEnabled: !!action.defaultSearchTranslationEnabled,
				defaultSearchTranslationLanguages: action.defaultSearchTranslationLanguages,
				defaultZoom: action.defaultZoom,
				desiredCrosstabsNarrativeOrder: action.desiredCrosstabsNarrativeOrder || [],
				disableFilters: action.disableFilters || false,
				disableFiltersMessage: action.disableFiltersMessage || false,
				disableGeneralDownload: action.disableGeneralDownload || false,
				dmaMap: !!action.dmaMap,
				duplicateToggle: action.duplicateToggle || false,
				emailSubscribeOption: action.emailSubscribeOption || false,
				entityFiltersAvailable: !action.hideEntities && action.entityFiltersAvailable,
				entityLanguage: action.entityLanguage,
				eventColumns: action.eventColumns || false,
				exoveraEditButton: !!action.exoveraEditButton,
				expandedContentShowTweets: !!action.expandedContentShowTweets,
				fakeSingleArticle: !!action.fakeSingleArticle,
				forceCrosstabsEntity: action.forceCrosstabsEntity || "",
				gaMeasurementId: action.gaMeasurementId || undefined,
				graphAudienceTooltipText: action.graphAudienceTooltipText || graphAudienceTooltipText,
				graphDataTypes: action.graphDataTypes || false,
				graphEventTooltipText: action.graphEventTooltipText || graphEventTooltipText,
				graphSourceTooltipText: action.graphSourceTooltipText || graphSourceTooltipText,
				graphSpendingTooltipText: action.graphSpendingTooltipText || graphSpendingTooltipText,
				graphTabFacets: action.graphTabFacets || false,
				graphTabActiveFacets,
				graphTabSelectorColumns: action.graphTabSelectorColumns || graphTabSelectorColumns,
				hideArticlesExport: action.hideArticlesExport || false,
				hideEntities: !!action.hideEntities,
				hideEntitiesInAIMode: !!action.hideEntitiesInAIMode,
				hideMap: !!action.hideMap,
				hideMapFilterIds: action.hideMapFilterIds || [],
				hideMapIndicatorInGraph: action.hideMap || !!action.hideMapIndicatorInGraph, // no indicator without a map
				hideParentNarrativeTags: !!action.hideParentNarrativeTags,
				hideShare: !!action.hideShare,
				highlightedEntitiesTitle: action.highlightedEntitiesTitle || "",
				hotArticleLimit: action.hotArticleLimit || hotArticleLimit,
				logRocketAppId: action.logRocketAppId || undefined,
				mapCountryIds: action.mapCountryIds || [],
				mapLookup: action.mapLookup || false,
				mapSourceTooltipText: action.mapSourceTooltipText || mapSourceTooltipText,
				mapAudienceTooltipText: action.mapAudienceTooltipText || mapAudienceTooltipText,
				mapEventTooltipText: action.mapEventTooltipText || mapEventTooltipText,
				mapSpendingTooltipText: action.mapSpendingTooltipText || mapSpendingTooltipText,
				mediaEntityFilters: !!action.mediaEntityFilters,
				mediaLanguages: action.mediaLanguages || ["English"],
				mediaCountries: action.mediaCountries || ["United States"],
				googleTranslateLanguages: action.googleTranslateLanguages || ["en"],
				mediaPredictiveSelect: !!action.mediaPredictiveSelect,
				mediaSearchHeaders: action.mediaSearchHeaders || defaultSearchHeaders,
				mediaTranslateTitles: !!action.mediaTranslateTitles,
				methodologyLink: action.methodologyLink || false,
				moeGraphAvailable: !!action.moeGraphAvailable,
				moeMapAvailable: !!action.moeMapAvailable,
				moeTabName: action.moeTabName || false,
				narrativeEntityTranslateToggle: !!action.narrativeEntityTranslateToggle,
				noUserSearches: !!action.noUserSearches,
				percentageTypes: action.percentageTypes || false,
				projectMapConfig: action.projectMapConfig,
				projectNotifications: action.projectNotifications || [],
				projectSpecificTagFormat: action.projectSpecificTagFormat || false,
				explorerDatasets: action.explorerDatasets || [],
				explorerShowEntities: action.explorerShowEntities || [],
				queryBuilderSupported: action.queryBuilderSupported,
				relevancySlider: !!action.relevancySlider,
				showAllTopicsMap: !!action.showAllTopicsMap,
				showTopicsInGraph: !!action.showTopicsInGraph,
				statsPDF: !!action.statsPDF,
				timezone: action.timezone || timezone,
				topicsPie: !!action.topicsPie,
				topicsPieArray: !!action.topicsPieArray,
				topicStatsConfig: action.topicStatsConfig || false,
				topPhrasesAvailable: !!action.topPhrasesAvailable,
				translateArticleTitles: !!action.translateArticleTitles,
				uiThemeToggle: !!action.uiThemeToggle,
				useGlobalMaps: !!action.useGlobalMaps || false,
				useInternalTranslation: !!action.useInternalTranslation,
				userSearchHideLocation: !!action.userSearchHideLocation,
				videoTabEntities: !!action.videoTabEntities,
				videoTabEntityGraph: !!action.videoTabEntityGraph,
				videoTabEndDate: action.videoTabEndDate || "",
				videoTabStartDate: action.videoTabStartDate || "",
				videoTabTopics: !!action.videoTabTopics,
				webPagesCollections: action.webPagesCollections,
			});
		case "receiveTrends":
			return Object.assign({}, state, {
				noReaction: action.noReaction, // This only applies to narrative based tabs
			});
		case SET_PROJECT_THEME:
			return Object.assign({}, state, {
				projectTheme: action.projectTheme,
			});
		case "setUITheme":
			return Object.assign({}, state, {
				darkUI: state.uiThemeToggle ? action.darkUI : !state.defaultLightUI,
			});
		case "facetSelect":
			const activeFacets = JSON.parse(JSON.stringify(state.graphTabActiveFacets));
			activeFacets[action.facet.id] = action.option;
			return Object.assign({}, state, {
				graphTabActiveFacets: activeFacets,
			});

		default:
			return state;
	}
};

export default projectConfig;
